import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import { shallow } from '@jwp/ott-common/src/utils/compare';
import type { PlaylistItem } from '@jwp/ott-common/types/playlist';
import { useUIStore } from '@jwp/ott-common/src/stores/UIStore';
import { useAccountStore } from '@jwp/ott-common/src/stores/AccountStore';
import { useConfigStore } from '@jwp/ott-common/src/stores/ConfigStore';
import { mediaURL } from '@jwp/ott-common/src/utils/urlFormatting';
import useFirstRender from '@jwp/ott-hooks-react/src/useFirstRender';
import useSearchQueryUpdater from '@jwp/ott-ui-react/src/hooks/useSearchQueryUpdater';
import { useSearch } from '@jwp/ott-hooks-react/src/useSearch';
import useOpaqueId from '@jwp/ott-hooks-react/src/useOpaqueId';
import CardGrid from '../../components/CardGrid/CardGrid';
import ErrorPage from '../../components/ErrorPage/ErrorPage';
import styles from './Search.module.scss';
import ApiService from '@jwp/ott-common/src/services/ApiService';
import { getModule } from '@jwp/ott-common/src/modules/container';
import useBreakpoint, { Breakpoint } from '@jwp/ott-ui-react/src/hooks/useBreakpoint';
import Button from '../../../../../packages/ui-react/src/components/Button/Button';
import { PLAYLIST_LIMIT } from '../../../../../packages/common/src/constants';

const Search = () => {
  const { t } = useTranslation(['search', 'video']);
  const { config, accessModel } = useConfigStore(({ config, accessModel }) => ({ config, accessModel }), shallow);
  const { siteName, features } = config;
  const headingId = useOpaqueId('search_heading');
  const firstRender = useFirstRender();
  const searchQuery = useUIStore((state) => state.searchQuery);
  const apiService = getModule(ApiService);
  const params = useParams();
  const breakpoint = useBreakpoint();
  const query = params['*'];
  const [updatedPlaylist, setUpdatedPlaylist] = useState<any>([]);
  const [showLoadMore, setShowLoadMore] = useState(true);
  const [pagenumber, setpagenumber] = useState(PLAYLIST_LIMIT);
  const { updateSearchQuery } = useSearchQueryUpdater();

  // playlist query
  const { isFetching, error, data: playlist } = useSearch(query || '');

  // this functionality is used to loadmore search results.
  const fetchMoreData = async () => {
    setpagenumber(pagenumber + PLAYLIST_LIMIT);

    let combinedPlayList;
    const fetchedPlaylist = await apiService.getPlaylistById(playlist?.feedid, {
      search: query,
      page_limit: PLAYLIST_LIMIT.toString(),
      page_offset: (pagenumber + 1).toString(),
    });

    if (!fetchedPlaylist?.playlist.length) {
      setShowLoadMore(false);
      return;
    }

    if (fetchedPlaylist) {
      if (updatedPlaylist?.playlist?.length) {
        combinedPlayList = updatedPlaylist?.playlist.concat(fetchedPlaylist?.playlist);
      } else {
        combinedPlayList = playlist?.playlist.concat(fetchedPlaylist?.playlist);
      }

      if (combinedPlayList) {
        fetchedPlaylist.playlist = combinedPlayList;
        setUpdatedPlaylist(fetchedPlaylist);
      }
    }
  };

  useEffect(() => {
    if (!query) {
      setUpdatedPlaylist([]);
      setpagenumber(PLAYLIST_LIMIT);
    }
  }, [query]); // this useeffect was used to reset the playlist on query value is null or undefined or empty.

  // User
  const { user, subscription } = useAccountStore(({ user, subscription }) => ({ user, subscription }), shallow);

  const getURL = (playlistItem: PlaylistItem) => mediaURL({ media: playlistItem, playlistId: features?.searchPlaylist });

  // Update the search bar query to match the route param on mount
  useEffect(() => {
    if (!firstRender) {
      return;
    }

    if (query && query !== searchQuery) {
      updateSearchQuery(query);
    }
  }, [firstRender, query, searchQuery, updateSearchQuery]);

  useEffect(() => {
    return () => {
      useUIStore.setState({
        searchQuery: '',
        searchActive: false,
      });
    };
  }, []);

  if ((error || !playlist) && !isFetching) {
    return (
      <ErrorPage title={t('error_heading')}>
        <h2 className={styles.subHeading}>{t('error_subheading')}</h2>
        <p>{t('error_description')}</p>
      </ErrorPage>
    );
  }

  if (!query) {
    return <ErrorPage title={t('start_typing')} />;
  }

  if (!playlist?.playlist.length) {
    return (
      <ErrorPage title={t('no_results_heading', { query })}>
        <h2 className={styles.subHeading}>{t('suggestions')}</h2>
        <ul>
          <li>{t('tip_one')}</li>
          <li>{t('tip_two')}</li>
          <li>{t('tip_three')}</li>
        </ul>
      </ErrorPage>
    );
  }

  // this is for hide load_more button in search page
  const showLoadMoreBtn = updatedPlaylist?.playlist?.length % 5 === 0 || updatedPlaylist?.playlist?.length === undefined;

  return (
    <div className={styles.search}>
      <Helmet>
        <title>
          {t('title', { count: playlist.playlist.length, query })} - {siteName}
        </title>
      </Helmet>
      <header className={styles.header}>
        <h2 id={headingId}>{t('heading')}</h2>
      </header>
      <CardGrid
        aria-labelledby={headingId}
        getUrl={getURL}
        playlist={updatedPlaylist?.playlist ? updatedPlaylist : playlist}
        isLoading={firstRender}
        accessModel={accessModel}
        isLoggedIn={!!user}
        hasSubscription={!!subscription}
      />
      {showLoadMore && showLoadMoreBtn && playlist?.playlist?.length >= 25 ? (
        <div className={styles.loadMoreBtn}>
          <Button
            label={t('video:Load_more')}
            aria-label={t('loadMore')}
            onClick={fetchMoreData}
            color={'default'}
            fullWidth={breakpoint < Breakpoint.md}
            size="large"
            type="submit"
          />
        </div>
      ) : null}
    </div>
  );
};

export default Search;
